import React, { useState, useEffect } from 'react';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Box } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Button } from '@mui/material';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../common/controls/LoadingSpinner';
import { useSnackbar } from '../../../context/SnackbarContext';
import { userHasAccess } from '../../../utils/userHasAccess';
import RequirePermission from '../../RequirePermission';
import { SUB_MODULES } from '../../../constants/subModulesConstants';
import { useUserOffices } from '../../../hooks/users/useUserOffices';
import { jwtDecode } from 'jwt-decode';
import ChangeUserType from '../forms/ChangeUserType';
import UserModules from './UserModules';
import InviteUserForm from './InviteUserForm';
import { useUserInvites } from '../../../hooks/users/useUserInvites';
import { useDeleteUserInvite } from '../../../hooks/users/useDeleteUserInvite';
import ConfirmDialog from '../../common/modals/ConfirmDialog';
import { companyTierToMaxUsers } from '../../../utils/companyTierToMaxUsers';
import MessageDialog from '../../common/modals/MessageDialog';
import { formatToLocalDate } from '../../../utils/dateFormatter';
import { useDeleteUserOffice } from '../../../hooks/users/useDeleteUserOffice';

export default function OfficeUsers() {

    const { t } = useTranslation('common');
    const { showSnackbar } = useSnackbar();
    const token = localStorage.getItem("accessToken");
    const userId = jwtDecode(token).id;
    const companyTier = jwtDecode(token).company_tier;
    const maxUsers = companyTierToMaxUsers(companyTier);
    const activeOfficeName = localStorage.getItem('activeOfficeName');

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmRemoveUserDialogOpen, setConfirmRemoveUserDialogOpen] = useState(false);
    const [messageDialogOpen, setMessageDialogOpen] = useState(false);

    const [open, setOpen] = React.useState(false);
    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
    };
    const [listTrigger, setListTrigger] = React.useState(false);
    const toggleListTrigger = () => {
        setListTrigger(prev => !prev);
    };

    const onRemoveInviteSuccess = () => {
        toggleListTrigger();
    };

    const onSaveInviteSuccess = () => {
        toggleListTrigger();
    };

    const { userOffices, loading } = useUserOffices(listTrigger);
    const { userInvites } = useUserInvites(listTrigger);
    const { remove } = useDeleteUserInvite(onRemoveInviteSuccess)

    const [selectedUserOffice, setSelectedUserOffice] = useState();

    const [formOpen, setFormOpen] = React.useState(false);
    const [formMode, setFormMode] = React.useState("create");
    
    const [inviteUserFormOpen, setInviteUserFormOpen] = useState(false);
    const [selectedUserInvite, setSelectedUserInvite] = useState(null);

    const [userModulesOpen, setUserModulesOpen] = useState(false);

    const handleChangeType = (userOffice) => {
        setSelectedUserOffice(userOffice);
        setFormMode("edit");
        setFormOpen(true);
    };

    const handleEditAccess = (userOffice) => {
      setSelectedUserOffice(userOffice);
      setUserModulesOpen(true);
    };

    const onRemoveUserOfficeSuccess = () => {
      showSnackbar(t('users.messages.remove_success'), "success");
      toggleListTrigger();
    };

    const { removeUserOffice } = useDeleteUserOffice(onRemoveUserOfficeSuccess); 

    const handleRemoveUser = async () => {
      setConfirmRemoveUserDialogOpen(false);
      await removeUserOffice(selectedUserOffice.id);
    };

    const handleAddUserInvite = () => {
      if (Number(userOffices.data.length) + Number(userInvites.data.length) >= Number(maxUsers))
      {
        setMessageDialogOpen(true);
      }
      else {
        setSelectedUserInvite(null);
        setInviteUserFormOpen(true);
      }

    };

    const handleCancelInvite = (userInvite) => {
      setConfirmDialogOpen(true);
      setSelectedUserInvite(userInvite);
    };

    const handleRemoveUserInvite = async () => {
      try {
          await remove(selectedUserInvite.id);
          showSnackbar(t('user_invites.messages.delete_success', { name: selectedUserInvite.name }), "success");
      } catch (err) {
          showSnackbar(t('user_invites.messages.delete_fail', { name: selectedUserInvite.name }), "error");
      }
      setConfirmDialogOpen(false);
    }

    return (
        <>
        <ListItem>
            <ListItemButton onClick={handleClickOpen}>
            <ListItemIcon>
                <LockPersonIcon fontSize='large' color='secondary' />
            </ListItemIcon>
            <ListItemText 
                primary="User Access"
                secondary="view and manage user access"
            />
            </ListItemButton>
        </ListItem>

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>

            <DialogTitle>
              <>
                <Typography fontSize={20} fontWeight={700}>User Access</Typography>
                <Typography fontSize={20} fontWeight={700}>{activeOfficeName} </Typography>
              </>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant='h6'>Registered Users</Typography>
                <List>
                  {loading ? (
                    <ListItem>
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <LoadingSpinner />
                      </Box>
                    </ListItem>
                  ) : userOffices.data?.length < 2 ? (
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="textSecondary">
                            no other users found
                          </Typography>
                        }
                      />
                    </ListItem>
                  ) : (
                    userOffices.data?.map((userOffice) =>
                      userOffice.user.id !== userId ? (
                        <ListItem
                          key={userOffice.id}
                          secondaryAction={
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <VerticalHamburgerMenu
                                options={[
                                  {
                                    id: 1,
                                    name: 'Change Account Type',
                                    callback: () => handleChangeType(userOffice),
                                    disabled: !userHasAccess(SUB_MODULES.USER_MANAGEMENT, 2),
                                  },
                                  {
                                    id: 2,
                                    name: 'Edit Access',
                                    callback: () => handleEditAccess(userOffice),
                                    disabled: !userHasAccess(SUB_MODULES.USER_MANAGEMENT, 2),
                                  },
                                  {
                                    id: 3,
                                    name: 'Remove User',
                                    callback: () => {
                                      setSelectedUserOffice(userOffice);
                                      setConfirmRemoveUserDialogOpen(true);
                                    },
                                    disabled: !userHasAccess(SUB_MODULES.USER_MANAGEMENT, 3),
                                  },
                                ]}
                              />
                            </Box>
                          }
                        >
                          <ListItemButton>
                            <ListItemText
                              primary={userOffice.user.name}
                              secondary={
                                <>
                                  <Typography variant="body2" component="span">
                                    {userOffice.user.email}
                                  </Typography>
                                  <br />
                                  <Typography variant="body2" component="span">
                                    type: {userOffice.type}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ) : null
                    )
                  )}
                </List>

                <Typography variant='h6'>Invited Users</Typography>
                <List>
                  {userInvites.data?.length < 1 ? (
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="textSecondary">
                            no invited users
                          </Typography>
                        }
                      />
                    </ListItem>
                  ) : (
                    userInvites.data?.map((userInvite) =>
                        <ListItem
                          key={userInvite.id}
                          secondaryAction={
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <VerticalHamburgerMenu
                                options={[
                                  {
                                    id: 1,
                                    name: 'Cancel Invitation',
                                    callback: () => handleCancelInvite(userInvite),
                                    disabled: !userHasAccess(SUB_MODULES.USER_MANAGEMENT, 3),
                                  },
                                ]}
                              />
                            </Box>
                          }
                        >
                          <ListItemButton>
                            <ListItemText
                              primary={userInvite.name}
                              secondary={
                                <>
                                  <Typography variant="body2" component="span">
                                    {userInvite.email}
                                  </Typography>
                                  <br />
                                  <Typography variant="body2" component="span">
                                    type: {userInvite.type}
                                  </Typography>
                                  <br />
                                  <Typography variant="body2" component="span">
                                    sent: {formatToLocalDate(userInvite.createdAt)}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                    )
                  )}
                </List>
            </DialogContent>
            <DialogActions>
              <RequirePermission subModuleName={SUB_MODULES.USER_MANAGEMENT} minAccessLevel={2}>
                <Button onClick={handleAddUserInvite}>Invite New User</Button>
              </RequirePermission>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <ChangeUserType 
          userOffice={selectedUserOffice}
          toggleUserListTrigger={toggleListTrigger}
          mode={formMode}
          open={formOpen}
          onClose={() => setFormOpen(false)}
        />
        <UserModules
          open={userModulesOpen}
          onClose={() => setUserModulesOpen(false)} 
          userOffice={selectedUserOffice}
        />
        <InviteUserForm 
          userOfficeData={userOffices.data}
          userInvitesData={userInvites.data}
          triggerCallback={toggleListTrigger}
          userInvite={selectedUserInvite}
          open={inviteUserFormOpen}
          onClose={() => setInviteUserFormOpen(false)}
        />
        <ConfirmDialog
          open={confirmDialogOpen}
          title="Cancel Invite"
          content="Are you sure you want to cancel this invitation?"
          confirmText="Cancel Invitation"
          cancelText="No"
          onConfirm={handleRemoveUserInvite}
          onCancel={() => setConfirmDialogOpen(false)}
        />
        <ConfirmDialog
          open={confirmRemoveUserDialogOpen}
          title={`Remove User`}
          content="Are you sure you want to remove this user? This action cannot be undone."
          confirmText="Remove User"
          cancelText="Cancel"
          onConfirm={handleRemoveUser}
          onCancel={() => setConfirmRemoveUserDialogOpen(false)}
        />
        <MessageDialog
          title='Max Number of Users Reached'
          content={`Your clinic has reached the maximum number of ${maxUsers} users.`}
          open={messageDialogOpen}
          onCancel={() => setMessageDialogOpen(false)} 
          cancelText='Go it'
        />
        </>
    );
}